/**
 * MIRROR of the client's `src/lib/cropGeometry.js` — keep the two in step.
 *
 * Admin and the app must frame a crop box identically, or reviewing an image
 * shows you something different from what users get. They're separate builds
 * with no shared package, so this is a deliberate copy: change one, change the
 * other.
 */
/**
 * Crop geometry — the one place that turns a stored crop box into CSS.
 *
 * A `cropBox` is `{x, y, w, h}` in NORMALIZED fractions of the image, so it
 * survives any uniform resize and can be compared against whatever variant the
 * CDN actually served.
 *
 * The subtle part is `object-position`. It is an ALIGNMENT, not a "centre on
 * this point": `object-position: 50% 80%` lines the point 80% down the image up
 * with the point 80% down the container. Setting it to the box's centre — which
 * is what the old cropCoverStyle did — therefore lands the crop in the wrong
 * place for every box that isn't already dead centre, and the error grows the
 * further off-centre it sits. That's why tall bottles with low labels rendered
 * as a close-up of the capsule.
 *
 * The correct percentage is derived below. It needs BOTH aspects — the image's
 * and the container's — which is exactly what the old "dimension-agnostic"
 * version didn't have.
 */

/** A box that covers (almost) the whole frame isn't a crop at all. */
export function isUsableBox(box) {
  if (!box) return false;
  if (!(box.w > 0) || !(box.h > 0)) return false;
  if (box.w >= 0.999 && box.h >= 0.999) return false;
  return true;
}

/**
 * With `object-fit: cover`, how much of the image is visible along each axis,
 * as a fraction. The axis that matches the container's aspect shows fully (1);
 * the other overflows and shows less.
 */
export function visibleFraction(imgW, imgH, containerW, containerH) {
  if (!(imgW > 0) || !(imgH > 0) || !(containerW > 0) || !(containerH > 0)) {
    return { x: 1, y: 1 };
  }
  const scale = Math.max(containerW / imgW, containerH / imgH);
  return {
    x: Math.min(1, containerW / (imgW * scale)),
    y: Math.min(1, containerH / (imgH * scale)),
  };
}

/**
 * The object-position percentage that centres the visible window on `centre`.
 *
 * The window spans `v` of the image and starts at `p * (1 - v)`, so centring it
 * on `c` means `p * (1 - v) + v / 2 = c`. Clamped to [0,1] because a box near
 * an edge can't be centred — it gets as close as the image allows, which is the
 * right behaviour (you see the box, just not centred).
 */
export function positionForCentre(centre, visible) {
  if (!(visible < 1)) return 0.5; // nothing overflows; position is irrelevant
  const p = (centre - visible / 2) / (1 - visible);
  return Math.min(1, Math.max(0, p));
}

/**
 * The CSS for showing a crop box inside a cover-fitted container.
 *
 * `imgW/imgH` should be the dimensions of the image ACTUALLY being displayed
 * (an `<img>`'s naturalWidth/naturalHeight), not what the database recorded.
 * The box is normalized, so it maps onto whatever variant was served — and
 * measuring what's on screen means a wrong stored dimension can't throw the
 * crop off, which is its own long-running bug.
 *
 * Falls back to a centred cover whenever anything needed is missing, which is
 * what an un-cropped image should look like anyway.
 */
export function coverCropStyle({
  box, imgW, imgH, containerW, containerH,
}) {
  const centred = { objectFit: 'cover', objectPosition: '50% 50%' };
  if (!isUsableBox(box)) return centred;
  if (!(imgW > 0) || !(imgH > 0) || !(containerW > 0) || !(containerH > 0)) return centred;

  const visible = visibleFraction(imgW, imgH, containerW, containerH);
  const cx = box.x + box.w / 2;
  const cy = box.y + box.h / 2;
  const px = positionForCentre(cx, visible.x) * 100;
  const py = positionForCentre(cy, visible.y) * 100;

  return {
    objectFit: 'cover',
    objectPosition: `${px.toFixed(1)}% ${py.toFixed(1)}%`,
  };
}

/**
 * Make an asset URL safe to measure.
 *
 * The CDN's `?w=N` is a WIDTH-ONLY resize: it leaves the height untouched, so
 * the bytes it returns are distorted. Measured empirically on production —
 * an 843x749 image served as `?w=240` comes back 240x749.
 *
 * That matters because any crop maths derived from the served image (its
 * naturalWidth/naturalHeight) would then be computed against a squashed
 * aspect. Adding a generous `h` plus `f=inside` keeps the width in charge
 * while preserving the aspect: `?w=800&h=4000&f=inside` on that same image
 * returns 800x711.
 *
 * `f=inside` also bakes in EXIF orientation, so a rotated phone photo reports
 * the dimensions it actually displays at — which is what the stored dimensions
 * record too.
 */
export function aspectSafeSrc(src) {
  if (!src || typeof src !== 'string') return src;
  if (src.includes('f=inside')) return src;

  const match = src.match(/[?&]w=(\d+)/);
  if (!match) return src; // untransformed: already the true image

  const width = parseInt(match[1], 10);
  if (!(width > 0)) return src;

  // Tall enough that width is always the binding constraint.
  return `${src}&h=${width * 8}&f=inside`;
}

/**
 * How wide a source image to request so a crop box can fill a tile.
 *
 * The box is often a small part of the photo — a label on a whole bottle — so
 * asking for a tile-sized image would upscale a fraction of it into the tile
 * and look soft. Asking for `tile / box.w` makes the BOX about tile-sized;
 * doubling covers retina.
 *
 * Capped at both ends: never below the tile itself, and never so large that a
 * thumbnail pulls down a full-resolution photo.
 */
export function coverSourceWidth(box, tilePx, { dpr = 2, cap = 1600 } = {}) {
  const base = Math.ceil(tilePx * dpr);
  if (!isUsableBox(box)) return Math.min(base, cap);
  // Guard tiny boxes, which would otherwise ask for something enormous.
  const fraction = Math.max(box.w, 0.08);
  return Math.min(cap, Math.max(base, Math.ceil(base / fraction)));
}

/**
 * Style for showing a crop box FILLING a tile — the wine-list thumbnail.
 *
 * This is a different job from `coverCropStyle`, and the difference matters.
 * `object-fit: cover` only POSITIONS the image; it never zooms. In a short wide
 * band that's right, because the visible slice is thin. In a square tile taken
 * from a portrait bottle, cover shows the whole width of the photo and about
 * three quarters of its height — so the label ends up small in the middle with
 * background around it, which is not a thumbnail of the label at all.
 *
 * Filling the tile with the box means scaling the image up so the box maps onto
 * the tile, and offsetting it so the box's centre lands in the tile's centre.
 * The returned style positions an <img> absolutely inside a tile that is
 * `position: relative; overflow: hidden`.
 *
 * `imgW/imgH` must be the dimensions of the image ACTUALLY displayed — measured,
 * not recorded. Sizing from a wrong stored dimension is what made a label crop
 * render as a close-up of the capsule.
 */
export function tileCropStyle({
  box, imgW, imgH, tile,
}) {
  if (!isUsableBox(box) || !(imgW > 0) || !(imgH > 0) || !(tile > 0)) return null;

  const aspect = imgH / imgW;
  // Scale so the box covers the tile on whichever axis is binding.
  const width = Math.max(tile / box.w, tile / (box.h * aspect));
  const height = width * aspect;

  return {
    position: 'absolute',
    width: `${Math.round(width)}px`,
    height: `${Math.round(height)}px`,
    left: `${Math.round(tile / 2 - (box.x + box.w / 2) * width)}px`,
    top: `${Math.round(tile / 2 - (box.y + box.h / 2) * height)}px`,
    maxWidth: 'none',
    // The explicit size already carries the image's true aspect, so this can
    // never distort — unlike the old `fill`, which distorted whenever the
    // dimensions it was given were wrong.
    objectFit: 'cover',
  };
}
